import React, { useEffect, useState } from "react";
import { Accessibility, X, Type, Contrast, Pause, Link2, RotateCcw } from "lucide-react";

/**
 * Minimal, self-contained accessibility widget.
 *
 * Adds 4 user-controllable settings, persisted in localStorage:
 *   - font-size: normal | large | xl
 *   - high-contrast: on | off
 *   - reduce-motion: on | off
 *   - underline-links: on | off
 *
 * Each setting toggles a data-attribute on <html>; index.css implements the
 * visual effects. No external dependencies.
 */
const STORAGE_KEY = "qht-a11y-v1";

const DEFAULTS = {
  fontSize: "normal",      // normal | large | xl
  highContrast: false,
  reduceMotion: false,
  underlineLinks: false,
};

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULTS };
  }
}

function applyToHtml(s) {
  const html = document.documentElement;
  html.dataset.a11yFont = s.fontSize;
  html.dataset.a11yContrast = s.highContrast ? "on" : "off";
  html.dataset.a11yMotion = s.reduceMotion ? "reduced" : "auto";
  html.dataset.a11yLinks = s.underlineLinks ? "underline" : "auto";
}

// Apply once at module load so the choice survives full-page reloads.
if (typeof window !== "undefined") applyToHtml(loadSettings());

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(loadSettings);

  useEffect(() => {
    applyToHtml(s);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  }, [s]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const reset = () => setS({ ...DEFAULTS });
  const isActive = JSON.stringify(s) !== JSON.stringify(DEFAULTS);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Bedienhilfen schließen" : "Bedienhilfen öffnen"}
        aria-expanded={open}
        data-testid="a11y-toggle"
        className="fixed z-[60] bottom-5 left-5 h-12 w-12 rounded-full bg-[hsl(var(--brand-green-dark))] text-white shadow-lg hover:bg-[hsl(var(--brand-green-strong))] focus:outline-none focus:ring-4 focus:ring-[hsl(var(--brand-green))]/40 transition-colors flex items-center justify-center"
      >
        {open ? <X className="h-5 w-5" /> : <Accessibility className="h-6 w-6" />}
        {isActive && !open && (
          <span aria-hidden="true" className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-amber-400 border-2 border-white" />
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Bedienhilfen für diese Webseite"
          data-testid="a11y-panel"
          className="fixed z-[60] bottom-20 left-5 w-[19rem] bg-white border border-brand-line shadow-xl"
        >
          <div className="px-5 py-4 border-b border-brand-line bg-[hsl(var(--brand-soft))]/40 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--brand-green-dark))] mb-0.5">Bedienhilfen</p>
              <h2 className="text-sm font-semibold text-neutral-900">Seite an Ihre Bedürfnisse anpassen</h2>
            </div>
          </div>

          <div className="p-5 space-y-5">
            {/* Schriftgröße */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Type className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" />
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-neutral-800">Schriftgröße</span>
              </div>
              <div role="radiogroup" aria-label="Schriftgröße wählen" className="grid grid-cols-3 gap-1.5">
                {[
                  { v: "normal", l: "Standard" },
                  { v: "large", l: "Groß" },
                  { v: "xl", l: "Sehr groß" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    role="radio"
                    aria-checked={s.fontSize === opt.v}
                    data-testid={`a11y-font-${opt.v}`}
                    onClick={() => setS({ ...s, fontSize: opt.v })}
                    className={`px-2 py-2 text-xs font-medium border transition-colors ${
                      s.fontSize === opt.v
                        ? "bg-[hsl(var(--brand-green-dark))] text-white border-[hsl(var(--brand-green-dark))]"
                        : "bg-white text-neutral-700 border-brand-line hover:border-[hsl(var(--brand-green))]"
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Toggles */}
            <ToggleRow
              icon={<Contrast className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" />}
              label="Hoher Kontrast"
              hint="Stärkere Farben für bessere Lesbarkeit"
              testId="a11y-contrast"
              checked={s.highContrast}
              onChange={(v) => setS({ ...s, highContrast: v })}
            />
            <ToggleRow
              icon={<Pause className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" />}
              label="Bewegungen reduzieren"
              hint="Übergänge und Animationen abschalten"
              testId="a11y-motion"
              checked={s.reduceMotion}
              onChange={(v) => setS({ ...s, reduceMotion: v })}
            />
            <ToggleRow
              icon={<Link2 className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" />}
              label="Links unterstreichen"
              hint="Verlinkungen deutlicher hervorheben"
              testId="a11y-links"
              checked={s.underlineLinks}
              onChange={(v) => setS({ ...s, underlineLinks: v })}
            />

            {isActive && (
              <button
                type="button"
                onClick={reset}
                data-testid="a11y-reset"
                className="w-full inline-flex items-center justify-center gap-2 text-xs font-medium text-neutral-600 hover:text-[hsl(var(--brand-green-dark))] py-2 border-t border-neutral-100 mt-1"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Auf Standard zurücksetzen
              </button>
            )}
          </div>

          <div className="px-5 py-3 border-t border-brand-line bg-neutral-50 text-[11px] text-neutral-500 leading-relaxed">
            Ihre Einstellungen werden lokal in Ihrem Browser gespeichert und gelten für künftige Besuche dieser Website.
            {" "}<a href="/barrierefreiheit" className="text-[hsl(var(--brand-green-dark))] underline">Erklärung zur Barrierefreiheit</a>
          </div>
        </div>
      )}
    </>
  );
}

function ToggleRow({ icon, label, hint, checked, onChange, testId }) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer select-none">
      <div className="flex items-start gap-2 flex-1">
        {icon}
        <div>
          <div className="text-sm text-neutral-900 font-medium">{label}</div>
          <div className="text-[11px] text-neutral-500 leading-snug">{hint}</div>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        data-testid={testId}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center transition-colors ${
          checked ? "bg-[hsl(var(--brand-green-dark))]" : "bg-neutral-300"
        }`}
      >
        <span
          aria-hidden="true"
          className={`inline-block h-3.5 w-3.5 transform bg-white shadow-sm transition-transform ${
            checked ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </button>
    </label>
  );
}
